import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-brand-gold font-sans font-semibold mb-4">
              Contact Us
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-brand-green leading-[0.95] mb-6">
              Let's Talk{" "}
              <span className="italic text-brand-gold">Business</span>
            </h1>
            <p className="text-brand-muted text-base sm:text-lg font-sans max-w-2xl leading-relaxed">
              Whether you're an importer, distributor, or wholesaler looking for
              premium Indian rice, we'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-form-section" className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Left - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green mb-8">
                Direct Contact
              </h3>
              <div className="flex flex-col gap-8">
                <a
                  href="tel:+919872227812"
                  data-testid="contact-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center shrink-0">
                    <Phone size={20} className="text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-brand-muted font-sans font-medium mb-1">
                      Phone
                    </p>
                    <p className="text-brand-green font-sans font-medium group-hover:text-brand-gold transition-colors">
                      +91-9872227812
                    </p>
                  </div>
                </a>

                <a
                  href="mailto:info@psimpex.in"
                  data-testid="contact-email"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center shrink-0">
                    <Mail size={20} className="text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-brand-muted font-sans font-medium mb-1">
                      Email
                    </p>
                    <p className="text-brand-green font-sans font-medium group-hover:text-brand-gold transition-colors">
                      info@psimpex.in
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-brand-gold/10 flex items-center justify-center shrink-0">
                    <MapPin size={20} className="text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-wider text-brand-muted font-sans font-medium mb-1">
                      Company
                    </p>
                    <p className="text-brand-green font-sans font-semibold text-lg">
                      P.S.IMPEX
                    </p>
                    <p className="text-brand-muted font-sans text-sm mt-1">
                      12029, Sahibzada Ajit Singh Nagar, Ludhiana, Punjab, India
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 p-6 bg-brand-sand rounded-2xl">
                <h4 className="font-serif text-lg font-medium text-brand-green mb-4">
                  Business Hours
                </h4>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-brand-muted">Monday - Saturday</span>
                    <span className="text-brand-green font-medium">9:00 AM - 6:00 PM IST</span>
                  </div>
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-brand-muted">Sunday</span>
                    <span className="text-brand-green font-medium">Closed</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <div className="bg-white p-8 lg:p-10 rounded-3xl shadow-[0_8px_32px_rgba(26,54,38,0.04)] border border-brand-gold/10">
                <h3 className="font-serif text-2xl sm:text-3xl font-medium text-brand-green mb-2">
                  Send Us a Message
                </h3>
                <p className="text-brand-muted text-sm font-sans mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-sans font-medium text-brand-green mb-2 block">
                        Full Name *
                      </label>
                      <Input
                        data-testid="contact-name-input"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                        className="bg-brand-bone border-brand-gold/20 focus:border-brand-gold rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-sans font-medium text-brand-green mb-2 block">
                        Email *
                      </label>
                      <Input
                        data-testid="contact-email-input"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        className="bg-brand-bone border-brand-gold/20 focus:border-brand-gold rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-sans font-medium text-brand-green mb-2 block">
                        Phone
                      </label>
                      <Input
                        data-testid="contact-phone-input"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 890"
                        className="bg-brand-bone border-brand-gold/20 focus:border-brand-gold rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-sans font-medium text-brand-green mb-2 block">
                        Company
                      </label>
                      <Input
                        data-testid="contact-company-input"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="bg-brand-bone border-brand-gold/20 focus:border-brand-gold rounded-xl"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-sans font-medium text-brand-green mb-2 block">
                      Message *
                    </label>
                    <Textarea
                      data-testid="contact-message-input"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements (rice type, quantity, destination)..."
                      required
                      rows={5}
                      className="bg-brand-bone border-brand-gold/20 focus:border-brand-gold rounded-xl resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={status === "loading"}
                    className="bg-brand-green hover:bg-brand-gold text-brand-bone font-sans font-semibold rounded-full py-6 transition-colors duration-300"
                  >
                    {status === "loading" ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send size={16} className="ml-2" />
                      </>
                    )}
                  </Button>

                  {status === "success" && (
                    <div data-testid="contact-success" className="flex items-center gap-2 text-green-700 bg-green-50 p-4 rounded-xl">
                      <CheckCircle size={18} />
                      <span className="text-sm font-sans">Thank you! We'll get back to you soon.</span>
                    </div>
                  )}
                  {status === "error" && (
                    <div data-testid="contact-error" className="flex items-center gap-2 text-red-700 bg-red-50 p-4 rounded-xl">
                      <AlertCircle size={18} />
                      <span className="text-sm font-sans">Something went wrong. Please try again.</span>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
